// themes
import { colors } from 'themes'
// constants
import { ENTITIES } from 'constants/entities'
import { OBJECT_ENTITY_NAMES } from 'constants/objects'
// types
import { IPlanItem, IPlanTypeLevel } from 'types/graphql'
// utils
import get from 'lodash/get'
import { filter, last, map, orderBy, pipe } from 'lodash/fp'
import { getKey, getNodeKey, getParentKey } from 'utils/tree'
import { getFlatDataFromTree, getTreeFromFlatData } from 'react-sortable-tree'

export const getPlanTypeLevel = (plan, entity) => {
  if (!plan) {
    return OBJECT_ENTITY_NAMES.OBJECTIVE
  }

  const planItems = get(plan, 'planItems.data', [])

  const levels = pipe((item) => get(item, 'planType.levels', []), orderBy('position', 'asc'))(plan)

  const entities = planItems.flatMap((item) =>
    ['planObjective', 'planAction', 'planKPI']
      .filter((key) => item[key])
      .map((key) => ({ ...item, entityId: item[key].id }))
  )

  const orderedFlatTree = orderBy('treePosition', 'asc')(entities)

  const tree = getTreeFromFlatData({
    flatData: orderedFlatTree,
    getKey,
    getParentKey,
    rootKey: null
  })

  const assignEntityLevels = (array, deepLevel = 0) => {
    array.forEach((item) => {
      const nextDeepLevel = item.referenced ? 0 : deepLevel + 1

      item.level = levels[deepLevel] || last(levels)

      if (item.children) {
        assignEntityLevels(item.children, nextDeepLevel)
      }
    })
  }

  assignEntityLevels(tree)

  const flatTreeWithLevels = pipe(
    (array) =>
      getFlatDataFromTree({
        treeData: array,
        getNodeKey: getNodeKey as any,
        ignoreCollapsed: false
      }),
    map('node')
  )(tree)

  return pipe(
    filter((item: any) => item.entityId === entity.id),
    (array) => get(array, '[0]'),
    (data) => {
      if (data?.level) {
        const typeOption = (data.level.typeOptions || []).find((option) => option.id === entity.typeOptionId)

        return {
          ...data.level,
          description: typeOption?.label || data.level.description || OBJECT_ENTITY_NAMES.OBJECTIVE
        }
      }
      return { description: OBJECT_ENTITY_NAMES.OBJECTIVE, color: colors.purple500 }
    }
  )(flatTreeWithLevels)
}

interface IDeepLevelContext {
  levels: IPlanTypeLevel[]
  planTypeLevelIds: string[]
}

// Recursively assigns level / nextLevel / planTypeLevelId / color to each tree node.
export const applyTreeDeepLevels = (
  array: (IPlanItem & any)[],
  { levels, planTypeLevelIds }: IDeepLevelContext,
  deepLevel = 0,
  color: string = null
) => {
  const lastLevel = last(levels)

  array.forEach((item) => {
    const nextLevel = item.referenced ? levels[deepLevel] : levels[deepLevel + 1]
    const nextDeepLevel = item.referenced ? 0 : deepLevel + 1

    const isObjective = item.entityType === ENTITIES.PLAN_OBJECTIVE

    let parentColor = color

    item.planTypeLevelId = planTypeLevelIds[deepLevel] || last(planTypeLevelIds)
    item.level = levels[deepLevel] || lastLevel
    // When the nesting goes deeper than the configured planType levels there is no real
    // next level. For non-referenced nodes we label it as a generic group and drop the
    // level's typeOptions (a group has no level options), keeping only style fields (e.g. color).
    item.nextLevel =
      nextLevel ||
      (item.referenced ? lastLevel : { ...lastLevel, description: OBJECT_ENTITY_NAMES.GROUP, typeOptions: [] })

    if (isObjective && item.entity.color) {
      item.color = item.entity.color
      parentColor = item.entity.color
    } else if (color) {
      item.color = color
    } else {
      item.color = item.level.color
    }

    if (item.children) {
      applyTreeDeepLevels(item.children, { levels, planTypeLevelIds }, nextDeepLevel, parentColor)
    }
  })
}

interface IResourceTotalsNode {
  children?: IResourceTotalsNode[]
  resourceBudget?: number
  resourceCost?: number
}

// Sums only the values that are actually present. An all-empty list stays `undefined` so the
// column keeps rendering an empty cell instead of a misleading 0.
const sumDefined = (values: (number | undefined)[]): number | undefined => {
  const defined = values.filter((value): value is number => typeof value === 'number')

  return defined.length ? defined.reduce((acc, value) => acc + value, 0) : undefined
}

// The backend returns `resourceBudget` / `resourceCost` per plan item — the totals of that item's
// own resources only. The tree columns show the whole branch, so each node's total becomes its own
// value plus the (already aggregated) totals of all its children. Returns a new tree; nodes without
// any value in their subtree keep an undefined total, while a real 0 is preserved.
export const applyTreeResourceTotals = <T extends IResourceTotalsNode>(array: T[]): T[] =>
  array.map((item) => {
    const children = item.children ? applyTreeResourceTotals(item.children as T[]) : item.children

    return {
      ...item,
      ...(item.children ? { children } : {}),
      resourceBudget: sumDefined([
        item.resourceBudget,
        ...(children || []).map(({ resourceBudget }) => resourceBudget)
      ]),
      resourceCost: sumDefined([item.resourceCost, ...(children || []).map(({ resourceCost }) => resourceCost)])
    }
  })
